// TASK-606 Door 2: canvas → workbench bridge. TASK-638: the workbook is now
// generated server-side (xlsxwriter + kielis_design branding: navy header,
// currency formats, totals row, frozen header) instead of a bare SheetJS dump.
//
// TASK-876: two changes, both about handover.
//
//  1. The component used to fetch positions itself and hardcode "Holdings analysis",
//     so it could be mounted on exactly the two widgets that show holdings and nowhere
//     else — while the ticket's whole point is that `Open as spreadsheet` IS the
//     handover primitive and every table should have it. It now takes the table it is
//     handed. `PortfolioHoldingsSpreadsheet` below keeps the original behaviour for the
//     callers that had it, so nothing at those two sites changed.
//  2. It stamps LINEAGE. A generated sheet used to record no source, so the workbench
//     banner read "Generated document — source unknown (no lineage was recorded)" —
//     accurate, and no use to the colleague receiving the link. It now names the widget
//     it came from, the client it is about and the instant the data was read.
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { portfolioApiService } from '../../../services/api/portfolioApiService';
import { generateWorkbenchDocument } from '../../../services/api/documentApi';

/** A column as the xlsx generator understands it (TASK-638 spec). */
export interface SheetColumn {
  header: string;
  width?: number;
  fmt?: 'integer' | 'currency' | 'percent';
  total?: 'sum';
}

export type SheetCell = string | number | null;

export interface SpreadsheetTable {
  /** Document title, and the sheet name unless `sheetName` overrides it. */
  title: string;
  sheetName?: string;
  columns: SheetColumn[];
  rows: SheetCell[][];
}

interface OpenAsSpreadsheetProps {
  /**
   * Builds the table at click time rather than on every render — a widget should not
   * pay for a spreadsheet nobody asked for, and the rows must reflect the data as it
   * is when the button is pressed, which is also what `as_of` will claim.
   */
  build: () => Promise<SpreadsheetTable | null> | SpreadsheetTable | null;
  /** Registry component name, recorded as provenance. */
  sourceWidget: string;
  clientId?: string;
  /**
   * The instant the underlying data was read, when the widget knows it — several already
   * do, and pass it to `WidgetContainer` as `dataAsOf`. Preferred over the click clock:
   * a sheet exported at 09:00 from a drift calculation timestamped 07:30 is as-of 07:30,
   * and recording 09:00 would overstate its currency on the one document meant to be
   * handed to someone who cannot check.
   */
  asOf?: string | null;
  label?: ReactNode;
}

export function OpenAsSpreadsheet({
  build,
  sourceWidget,
  clientId,
  asOf,
  label = 'Open as spreadsheet',
}: OpenAsSpreadsheetProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const table = await build();
      if (!table || table.rows.length === 0) {
        toast.info('Nothing to export from this view.');
        return;
      }
      // The widget's own data timestamp when it has one; otherwise the read clock,
      // captured BEFORE the request so it describes when the data was read rather than
      // when the server finished writing the file.
      const asOfIso = asOf ?? new Date().toISOString();
      const res = await generateWorkbenchDocument({
        format: 'xlsx',
        title: table.title,
        client_id: clientId,
        lineage: { source_widget: sourceWidget, as_of: asOfIso },
        spec: {
          sheets: [
            {
              name: table.sheetName ?? table.title.slice(0, 31),
              table: { columns: table.columns, rows: table.rows },
            },
          ],
        },
      });
      navigate(`/workbench/${res.document_id}`);
    } catch (err) {
      toast.error(
        `Couldn't open as spreadsheet: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => void run()}
      title="Open as spreadsheet"
      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

/**
 * The original holdings export, unchanged in behaviour for its two existing call sites.
 *
 * TASK-876: `Position id` is now the first column. A handover is a document plus a
 * sentence — "check row 12" — and a row that carries no identity cannot be referred to
 * across a rename, a re-sort or a re-export. The id is the thing a comment, a task or a
 * later diff can be pinned to; the security name is not, because two custodians spell
 * the same instrument differently and the same book can hold one instrument twice.
 */
export function PortfolioHoldingsSpreadsheet({
  clientId,
  sourceWidget = 'CockpitPortfolioView',
}: {
  clientId: string;
  /**
   * The registry component this is mounted on. Required because it is mounted on TWO —
   * `CockpitPortfolioView` and `RiskAnalytics` — and hardcoding the first meant a sheet
   * exported from Risk told its reader it came from a view it did not come from. That is
   * the failure `LineageRequest.source_widget` is documented against: printing a wrong
   * provenance is worse than printing none, because none is visibly absent and wrong is
   * not. Defaulted rather than mandatory so the common caller stays a one-liner.
   */
  sourceWidget?: string;
}) {
  return (
    <OpenAsSpreadsheet
      sourceWidget={sourceWidget}
      clientId={clientId}
      build={async () => {
        const { positions } = await portfolioApiService.getPositions(clientId);
        const rows = (positions ?? []).map((p) => [
          p.id ?? '',
          p.security_name ?? '',
          p.isin ?? '',
          p.asset_class ?? '',
          p.quantity ?? null,
          p.market_value ?? null,
          p.currency ?? '',
        ]);
        return {
          title: 'Holdings analysis',
          sheetName: 'Holdings',
          columns: [
            { header: 'Position id', width: 38 },
            { header: 'Security', width: 32 },
            { header: 'ISIN', width: 16 },
            { header: 'Asset class', width: 18 },
            { header: 'Quantity', fmt: 'integer', width: 12 },
            { header: 'Market value', fmt: 'currency', width: 18, total: 'sum' },
            { header: 'Currency', width: 10 },
          ],
          rows,
        };
      }}
    />
  );
}
